"use client";

import { siteHref } from "../current";
import { useState, useTransition } from "react";
import { setComingSoon } from "../actions";
import { BTN_GHOST, BTN_PRIMARY, CARD, Notice } from "./ui";

// Settings → the coming-soon switch (lib/launch.ts). Both directions ask once more: the change is public at once.
export function LaunchSwitch({ on: initial }: { on: boolean }) {
  const [on, setOn] = useState(initial);
  const [asking, setAsking] = useState(false);
  const [pending, start] = useTransition();
  const [notice, setNotice] = useState<{ type: "ok" | "error"; text: string } | null>(null);

  const apply = () =>
    start(async () => {
      const next = !on;
      const result = await setComingSoon(next);
      setAsking(false);
      if (!result.ok) return setNotice({ type: "error", text: result.error });
      setOn(next);
      setNotice({ type: "ok", text: next ? "Coming-soon page is on. Visitors now see only the game." : "The website is live. Visitors now see every page." });
    });

  return (
    <section className={`${CARD} flex flex-col gap-4`} aria-labelledby="launch-title">
      <div className="flex flex-wrap items-center gap-3">
        <h2 id="launch-title" className="text-lg font-bold">
          Coming-soon page
        </h2>
        <span className={`rounded-full px-3 py-1 font-mono text-xs font-medium tracking-[0.1em] uppercase ${on ? "bg-amber text-midnight" : "bg-teal/15 text-teal"}`}>
          {on ? "On: site hidden" : "Off: site live"}
        </span>
      </div>
      <p className="max-w-2xl text-sm text-midnight/75">
        While it&apos;s on, visitors see only the coming-soon game (Launch Run) at <bdi>/</bdi> and <bdi>/ar</bdi>, and every other page address
        takes them there. You still see the whole site while you&apos;re signed in here. You can always preview the game at{" "}
        <a href={siteHref("/coming-soon")} target="_blank" className="font-bold text-teal underline underline-offset-4">
          /coming-soon
        </a>
        .
      </p>
      {asking ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-bold">{on ? "Launch the website for everyone now?" : "Hide the website behind the coming-soon game?"}</span>
          <button type="button" onClick={apply} disabled={pending} className={BTN_PRIMARY}>
            {pending ? "Saving…" : on ? "Yes, launch the site" : "Yes, show coming soon"}
          </button>
          <button type="button" onClick={() => setAsking(false)} disabled={pending} className={BTN_GHOST}>
            Cancel
          </button>
        </div>
      ) : (
        <div>
          <button type="button" onClick={() => setAsking(true)} className={on ? BTN_PRIMARY : BTN_GHOST}>
            {on ? "Turn off and launch the site" : "Turn on the coming-soon page"}
          </button>
        </div>
      )}
      <Notice notice={notice} />
    </section>
  );
}
